import { useEffect, useState } from "react";
import { Reveal } from "./Reveal";
import { Check, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice, formatApiErrorDetail } from "@/lib/api";

const PROGRAM_BY_ID = {
  "pkg-inperson": "in-person",
  "pkg-hybrid": "hybrid",
  "pkg-online": "online",
  "pkg-bootcamp": "bootcamp",
};

function PackageCard({ pkg, onConsult, onSelectPlan }) {
  const hasVariants = Array.isArray(pkg.variants) && pkg.variants.length > 0;
  const recommendedVariant = hasVariants ? pkg.variants.find((v) => v.recommended) : null;
  const [variantKey, setVariantKey] = useState(
    hasVariants ? (recommendedVariant?.key || pkg.variants[0].key) : null
  );
  const activeVariant = hasVariants ? pkg.variants.find((v) => v.key === variantKey) : null;
  const termList = hasVariants ? activeVariant.terms : pkg.terms;

  const defaultMonths = termList.find((t) => t.months === 3)?.months || termList[0]?.months;
  const [months, setMonths] = useState(defaultMonths);
  const [loading, setLoading] = useState(false);

  const term = termList.find((t) => t.months === months) || termList[0];
  const monthly = term ? term.price_cents / term.months : 0;
  const baseMonthly = termList.find((t) => t.months === 1)?.price_cents || 0;
  const savingsPct =
    baseMonthly && months > 1 ? Math.round((1 - monthly / baseMonthly) * 100) : 0;
  const isDirect = pkg.checkout_mode === "direct";
  const variantSuffix = activeVariant ? ` (${activeVariant.label})` : "";

  const handleClick = async () => {
    if (!isDirect) {
      onSelectPlan?.({
        program: PROGRAM_BY_ID[pkg.id] || "",
        note: `Interested in ${pkg.name}${variantSuffix} — ${months} month plan (${formatPrice(
          term.price_cents,
          pkg.currency
        )})`,
      });
      onConsult?.();
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", { package_id: pkg.id, months, variant_key: variantKey });
      window.location.href = data.url;
    } catch (e) {
      toast(formatApiErrorDetail(e.response?.data?.detail), {
        description: "Let's set up your spot directly with the coach.",
      });
      onConsult?.();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`group relative flex h-full flex-col p-7 md:p-8 border border-[#222] ${
        pkg.featured
          ? "bg-[#0b0f1a] lg:z-10 shadow-[0_0_60px_rgba(0,94,234,0.18)] lg:-my-2"
          : "bg-[#080808] hover:bg-[#0b0b0b]"
      } transition-colors`}
      data-testid={`pricing-card-${pkg.id}`}
    >
      {pkg.featured && (
        <span className="absolute top-0 right-0 bg-[#005EEA] text-white font-mono-accent text-[10px] px-4 py-2">
          Most Popular
        </span>
      )}
      <p className="font-mono-accent text-[10px] text-[#666] mb-4">{pkg.tagline}</p>
      <h3 className="font-display text-white text-2xl leading-none mb-6">{pkg.name}</h3>

      {/* Frequency selector (variants) */}
      {hasVariants && (
        <div className="mb-4" data-testid={`variant-selector-${pkg.id}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="font-mono-accent text-[9px] text-[#555]">Sessions per week</p>
            {recommendedVariant && (
              <span className="font-mono-accent text-[9px] text-[#005EEA]">★ Best value: {recommendedVariant.key}</span>
            )}
          </div>
          <div className="grid grid-cols-4 gap-1">
            {pkg.variants.map((v) => (
              <button
                key={v.key}
                onClick={() => setVariantKey(v.key)}
                className={`relative py-2 font-mono-accent text-[10px] border transition-colors ${
                  variantKey === v.key
                    ? "border-[#005EEA] bg-[#005EEA] text-white"
                    : "border-[#222] text-[#888] hover:border-[#444] hover:text-white"
                } ${v.recommended ? "ring-1 ring-[#005EEA]/40" : ""}`}
                data-testid={`variant-${pkg.id}-${v.key}`}
              >
                {v.key}
                {v.recommended && (
                  <span className="absolute -top-1.5 -right-1.5 h-2 w-2 rounded-full bg-[#005EEA]" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Duration selector */}
      <div className="grid grid-cols-5 gap-1 mb-6" data-testid={`term-selector-${pkg.id}`}>
        {termList.map((t) => (
          <button
            key={t.months}
            onClick={() => setMonths(t.months)}
            className={`py-2 font-mono-accent text-[10px] border transition-colors ${
              months === t.months
                ? "border-[#005EEA] bg-[#005EEA] text-white"
                : "border-[#222] text-[#888] hover:border-[#444] hover:text-white"
            }`}
            data-testid={`term-${pkg.id}-${t.months}`}
          >
            {t.months}M
          </button>
        ))}
      </div>

      <div className="mb-2 flex items-end gap-2">
        <span className="font-display text-white text-5xl leading-none" data-testid={`price-${pkg.id}`}>
          {formatPrice(term.price_cents, pkg.currency)}
        </span>
        <span className="text-[#666] text-sm mb-1">/ {months} mo</span>
      </div>
      <div className="mb-8 flex items-center gap-2 flex-wrap">
        <p className="text-[#555] text-xs font-mono-accent">
          ≈ {formatPrice(monthly, pkg.currency)} / month
        </p>
        {savingsPct > 0 && (
          <span
            className="font-mono-accent text-[9px] text-[#005EEA] border border-[#005EEA]/40 px-2 py-0.5"
            data-testid={`savings-${pkg.id}`}
          >
            Save {savingsPct}%
          </span>
        )}
      </div>

      <ul className="flex-1 space-y-3 mb-8">
        {pkg.features.map((f, idx) => (
          <li key={idx} className="flex items-start gap-3 text-[#A0A0A0] text-sm">
            <Check size={15} className="text-[#005EEA] mt-0.5 shrink-0" />
            {f}
          </li>
        ))}
      </ul>

      <button
        onClick={handleClick}
        disabled={loading}
        className={`font-mono-accent text-xs px-6 py-5 flex items-center justify-center gap-2 transition-colors disabled:opacity-60 ${
          pkg.featured
            ? "bg-[#005EEA] text-white hover:bg-[#0047b3]"
            : "border border-white/25 text-white hover:bg-white hover:text-black"
        }`}
        data-testid={`buy-btn-${pkg.id}`}
      >
        {loading ? (
          <Loader2 size={14} className="animate-spin" />
        ) : isDirect ? (
          "Join Now"
        ) : (
          <>
            Request This Plan <ArrowRight size={13} />
          </>
        )}
      </button>
      {!isDirect && (
        <p className="mt-3 text-center text-[#555] text-[10px] font-mono-accent">
          Coach sends a secure invoice after your consult
        </p>
      )}
    </div>
  );
}

export default function Pricing({ onConsult, onSelectPlan }) {
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  return (
    <section id="pricing" className="bg-[#050505] py-24 md:py-40 border-t border-[#222]" data-testid="pricing">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <Reveal className="mb-14 md:mb-20">
          <p className="font-mono-accent text-[#005EEA] text-xs mb-4">Coaching Packages</p>
          <h2 className="font-display text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9] max-w-4xl">
            Choose your commitment
          </h2>
          <p className="text-[#A0A0A0] text-lg mt-6 max-w-xl">
            Pick a plan length that fits your goals — the longer you commit, the better the value.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {packages.map((pkg, i) => (
            <Reveal key={pkg.id} delay={i * 0.08}>
              <PackageCard pkg={pkg} onConsult={onConsult} onSelectPlan={onSelectPlan} />
            </Reveal>
          ))}
        </div>

        <p className="mt-8 text-center text-[#666] text-sm max-w-2xl mx-auto leading-relaxed">
          <span className="text-[#005EEA] font-mono-accent text-[10px]">Flexible payment plans available</span>
          <br />
          Afterpay is available on eligible coaching and bootcamp packages, so you can spread your
          payments over time while starting your fitness journey today. Secure checkout powered by Square.
        </p>
      </div>
    </section>
  );
}
